import React, { useEffect, useState } from 'react';
import { catchLinks, go, NAV_EVENT, goBack } from './nav.js';
import { createRoot } from 'react-dom/client';
import { keepSignedIn, PREVIEW_EVENT, getToken } from './api.js';
import { readCentre, restoreHome } from './place.js';
import Preview from './Preview.jsx';
import './eleat.css';
import SignIn from './SignIn.jsx';
import Venue from './Venue.jsx';
import Home from './Home.jsx';
import Explore from './Explore.jsx';
import ViewAll from './ViewAll.jsx';
import Saved from './Saved.jsx';
import Profile from './Profile.jsx';
import Zaryna from './Zaryna.jsx';
import Join from './Join.jsx';
import Reset from './Reset.jsx';
import Preferences from './Preferences.jsx';
import Help from './Help.jsx';
import Notifications from './Notifications.jsx';
import Reserve from './Reserve.jsx';
import Reservations from './Reservations.jsx';
import Welcome from './Welcome.jsx';
import Splash from './Splash.jsx';
import { Boundary } from './Boundary.jsx';
import { reloadIfStale, watchForNewBuilds } from './fresh.js';
import Location from './Location.jsx';
import LocationAsk from './LocationAsk.jsx';
import Reviews from './Reviews.jsx';
import { JoinSheet } from './gate.jsx';
import { AppBar, AfterJoinApp } from './getapp.jsx';
import BookAsk from './bookask.jsx';
import { startI18n } from './i18n.js';
import { claimPendingVisit } from './visit.js';
import Legal from './Legal.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ STILL NOT A ROUTER, AND THAT IS DELIBERATE — 22 September 2026.
 *
 * Eleat's navigation is the tab bar: Home · Explore · Zaryna · Saved ·
 * Profile. Two of those five exist. Installing a router now would be building
 * the shape of an app around the screens that happen to be finished, and the
 * shape would be wrong by the time the rest arrive.
 *
 * So: no path, no query → Home. `?explore=1` is Explore, `?all=<type>` one of
 * the five View All lists. `?venue=<id>` opens
 * a venue, `&tab=` at a particular tab. `?signin=1` is the sign-in screen
 * until Profile exists.
 *
 * ⚠️ THIS HEADER USED TO DESCRIBE A SCREEN THAT NO LONGER EXISTS. It opened
 * "THIS IS A WAY IN, NOT NAVIGATION" and explained a temporary list called
 * the Doorway, for some minutes after Home replaced it. A comment left behind
 * by the thing it described is the trap this repository keeps setting for
 * itself — the phase gate, the migration status line, the review-eligibility
 * example. Rewritten in the same breath as the change.
 * ══════════════════════════════════════════════════════════════════════════ */

function App() {
  const params = new URLSearchParams(window.location.search);
  /* ══════════════════════════════════════════════════════════════════════
   * ⚠️ THE FRONT DOOR — AL, 30 SEPTEMBER 2026
   *
   * *"visitors should not have to sign up at all. They should just be able
   * to go in… as soon as you go to www.eleat.app, you should go splash →
   * homepage… if you are looking at reserving, putting a heart, putting a
   * flag, writing a review, in the feeds… then it'll prompt you to either
   * sign in or become an Eleater at that time. So it's not right away."*
   *
   * So the bare URL is Home for EVERYBODY, signed in or not. The account is
   * asked for at the first action that needs one, by `needsAccount()` in
   * gate.jsx, and the Welcome door is reached from there (`?welcome=1`) or
   * from Profile — the locked flow's own shape: 02_Welcome.html says *"Nobody
   * reaches this screen without having already browsed: you arrive here from
   * the gate, or from Sign in on the Me tab."*
   *
   * ⚠️ SIGNED IN STILL NEVER SEES A DOOR. Welcome, Sign in and Join each send
   * somebody who already has a token on to where they were going.
   * ══════════════════════════════════════════════════════════════════════ */
  const venueId = params.get('venue');
  const tab = params.get('tab') || 'about';

  /* ⚠️ RESERVE IS ITS OWN SCREEN AND IT IS CHECKED BEFORE `venue`, because
     both carry a venue id and the first match wins. Eleat's own form runs
     BEFORE the hand-off — once the Eleater is on the provider's page Eleat is
     blind, so this screen is everything Eleat will ever know about the
     booking. */
  /* the private preview's password screen (17 Oct 2026) — only ever reached when the server says 423 */
  if (params.get('preview')) return <Preview />;
  if (params.get('reserve')) return <Reserve id={params.get('reserve')} />;
  if (venueId) {
    return <Venue id={venueId} tab={tab} onBack={() => { goBack('?'); }} />;
  }
  if (params.get('signin')) return <SignIn />;
  if (params.get('welcome')) return <Welcome />;
  /* ⚠️ THE TWO DOORS THAT DID NOT EXIST UNTIL 21 SEPTEMBER 2026. Sign-in
     carried a "New to Eleat? Create an account" button whose whole behaviour
     was to print "Not built yet", so nobody could join and nobody could
     recover a password — every React screen built before today was for people
     who already had an account. */
  if (params.get('join')) return <Join />;
  if (params.get('reset')) return <Reset />;
  if (params.get('preferences')) return <Preferences />;
  if (params.get('help')) return <Help />;
  if (params.get('legal')) return <Legal />;
  if (params.get('reservations')) return <Reservations />;
  if (params.get('location')) return <Location />;
  if (params.get('reviews')) return <Reviews />;
  /* ⚠️ TWO DIFFERENT SCREENS, AND THE NAMES ARE NEARLY THE SAME — so the
     distinction is written down rather than left to whoever edits this next.
     `?notifications=1` is WHAT HAPPENED, the list of notices (Profile).
     `?notifsettings=1` is HOW YOU WANT TO BE TOLD, the preferences. The locked
     screens keep them apart and each links to the other. */
  if (params.get('notifsettings')) return <Notifications />;
  if (params.get('explore')) return <Explore />;
  if (params.get('all')) return <ViewAll which={params.get('all')} />;
  if (params.get('saved')) return <Saved />;
  if (params.get('zaryna')) return <Zaryna />;
  if (params.get('profile')) return <Profile />;
  if (params.get('settings')) return <Profile view="settings" />;
  if (params.get('notifications')) return <Profile view="notifications" />;
  /* ⚠️ NO WALL HERE ANY MORE — see the front-door rule above. Until
     30 September this line sent everybody without an account to Welcome, so
     a visitor to www.eleat.app saw a sign-up screen and not one restaurant.

     ⚠️ HOME IS THE LANDING SCREEN NOW — 22 September 2026. The Doorway below
     called itself "a way in, not navigation" and said so on screen, under a
     list of restaurants, on Al's phone. It was a door held open until Home
     existed. Home exists. */
  return <Home />;
}

/* ⚠️ THE SCREEN FOLLOWS THE ADDRESS WITHOUT A PAGE LOAD — 1 October 2026.
   See nav.js. `key` is the address, so every screen is built fresh exactly
   as a page load built it; only the reload itself — the page, the code, the
   splash — is gone. The back button arrives as `popstate`. */
function Screens() {
  const [address, setAddress] = useState(() => window.location.search);
  useEffect(() => {
    const follow = () => setAddress(window.location.search);
    window.addEventListener('popstate', follow);
    window.addEventListener(NAV_EVENT, follow);
    /* the server said the preview password is needed (api.js) → the password screen, once */
    const preview = () => { if (!/[?&]preview=1/.test(window.location.search)) go('?preview=1', { replace: true }); };
    window.addEventListener(PREVIEW_EVENT, preview);
    return () => {
      window.removeEventListener('popstate', follow);
      window.removeEventListener(NAV_EVENT, follow);
      window.removeEventListener(PREVIEW_EVENT, preview);
    };
  }, []);
  return <App key={address} />;
}

/* ⚠️ THE DOORWAY IS GONE — 22 September 2026, replaced by Home.jsx.
   It existed because no screen could list venues; it fetched a raw search and
   rendered rows with "Temporary — a way into the venue page until Home is
   built" printed beneath the Eleat mark. Deleted rather than left behind a
   flag: a temporary screen nobody can reach is dead code that still has to be
   read by whoever comes next. Its one real lesson is carried into Home — the
   MARKET IS NAMED, NEVER GUESSED, because a country is not a market. */

/* ⚠️ THE SPLASH WRAPS THE APP, IT DOES NOT ROUTE TO IT. It holds the curtain
   for 3.6s on the first open of a session and then renders whatever App had
   already decided — Home for somebody signed in, Welcome for somebody not.
   Putting it in the route table would have made it a fourth thing competing to
   be the landing screen, and a second copy of Al's front-door rule. */
/* ⚠️ THE BOUNDARY IS OUTSIDE THE SPLASH, so a crash during the splash is
   caught too. React empties `#root` when anything throws during render, and a
   blank screen is indistinguishable from a dead server, a bad connection, or a
   page that never loaded — Al, 23 September 2026: *"the app doesn't open up on
   my phone. it's nothing's happening."*  It could not be diagnosed because
   there was nothing on screen to diagnose. */
/* ⚠️ FIRED AND FORGOTTEN, BEFORE THE FIRST PAINT IS WAITED ON. If the browser
   is holding an `index.html` from before the last deploy it is asking for a
   bundle that may no longer exist, and what it gets back is anybody's guess —
   which is how a phone that had the app open across a deploy ends up showing
   nothing at all. This asks the server what is current and reloads once if the
   answer disagrees. It never blocks the app from starting. */
reloadIfStale();
/* ⚠️ AND EVERY TIME THE TAB COMES BACK. Running this once at startup was
   not enough: Android Chrome restores a backgrounded tab WITHOUT re-running
   any script, so a phone with nineteen tabs open can sit two deploys behind
   for days — which is exactly what Al's screenshot showed. */
watchForNewBuilds();

/* A chosen city is remembered across opens — Al, 2 Oct 2026; see place.js. */

/* every <a href="?…"> to another screen switches in place — nav.js */
catchLinks();

/* a visit the restaurant gave you at the door (Lite's walk-in, 171) */
claimPendingVisit();

/* ⚠️ THE ELEATER'S LANGUAGE BEFORE THE FIRST SCREEN DRAWS (5 Oct 2026) —
   English, 繁體中文 or 简体中文, the one they chose or their phone's. The
   translator watches the page from here on, so every screen, sheet and
   message is in their language as it appears (i18n.js). */
startI18n();

/* a sign-in is renewed once a day while used — it never runs out (api.js) */
keepSignedIn();
/* signed in already, and this browser has no city chosen: their account's
   Eleat city (Al-Noor, 31 Oct 2026: "remember me") — no welcome line here */
{
  const c = readCentre();
  if (getToken() && !(c && c.from === 'chosen')) restoreHome({ welcome: false });
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Boundary>
      <Splash>
        {/* ⚠️ ASKED ONCE, AT THE TOP, ON WHATEVER SCREEN THEY LAND ON.
            Al, 24 September 2026: *"right now its still showing location. not
            my current location. so thats a no no. you have to get that working
            where ever i am on this planet."*

            Finding somebody has been built since `Location.jsx` was written —
            it simply never ran unless they walked to that screen and pressed a
            button, which nobody does. So Home said "Set location" to every
            person who had never been there, which is everybody. **The code was
            right and the moment was wrong.**

            ⚠️ IT SITS HERE RATHER THAN IN Home BECAUSE A LINK CAN LAND ANYWHERE.
            A shared venue opens on `?venue=…`, and that page measures distance
            too. One place asks; every screen benefits.

            ⚠️ AND IT USUALLY DRAWS NOTHING. Already located, or already
            allowed, or already refused — three of its four answers put nothing
            on screen. */}
        <LocationAsk />
        <Screens />
        {/* the one "Become an Eleater" sheet — opened by needsAccount() from
            any screen, so it is mounted once, here, above all of them */}
        <JoinSheet />
        {/* the app push — off until the store links exist (getapp.jsx) */}
        <AppBar />
        <AfterJoinApp />
        <BookAsk />
      </Splash>
    </Boundary>
  </React.StrictMode>
);
